import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { htmlToText } from 'html-to-text';

import DOMPurify from 'dompurify';
import nl2br from 'react-br';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';

import IconButton from '@mui/joy/IconButton';
import Stack from '@mui/joy/Stack';
import Tooltip from '@mui/joy/Tooltip';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Chip from '@mui/joy/Chip';
import Divider from '@mui/joy/Divider';

import {TimeUI, DateOrTime} from './Date';

import IdentityAvatar from './IdentityAvatar';

import Linkify from 'linkify-react';
import { getIcon } from 'utils/misc';
import { capitalize } from 'utils/misc';
import { Success } from './FancyToast';
import { FileAssociationType } from 'stores/file_association_store';
import FileItem from 'components/FileItem';
import RatingCardThumbs from 'components/RatingCardThumbs';
import useRating from 'hooks/useRating';
import useUserIdentity from 'hooks/useUserIdentity';
import useFHRequest from 'hooks/useFHRequest';

type ChatBubbleProps =  {
    uuid?: string;
    sender?: string;
    content: string | React.ReactNode;
	content_type: string;
    context_type?: string;
    context_uuid?: string;
    original_content?: string;
    date_created: string;
    identityKey?: string;
    omitIdentity?: boolean;
    file_associations?: FileAssociationType[];
};


// type LinkType = {
//     href: string;
//     // target: string;
//     // rel: string;
//     // title: string;
//     // className: string;
//     // onClick: string;
//     // children: string;
//     // content: string;
// };

const LinkTooltip = ({ link, cropped }: { link: string, cropped: string }) => {
    const CopyIcon = getIcon('ContentCopy');
    return (
        <Stack
            alignItems="center"
            m={2} spacing={1}>

            <Tooltip
                placement="top"
                arrow
                title="Open link in new tab">
                <a target="_blank" href={link}>{link}</a>
            </Tooltip>

            <Tooltip arrow title="Copy full link to clipboard">
                <Typography
                    endDecorator={<CopyIcon />}
                    sx={{ cursor: 'default' }}
                    onClick={() => {
                        navigator.clipboard.writeText(link)
                        Success("Copied to clipboard");
                    }}
                    level="body-xs">{cropped}</Typography>
            </Tooltip>
        </Stack>
    );
};


const renderLink = ({ attributes, content } : { attributes: { [attr: string]: any; }, content:string }) => {
    const { href } = attributes;
    const IconLink = getIcon('Link');
    const EmailIcon = getIcon('MailOutline');

    // console.log('renderLink', content, attributes);
    if (href.startsWith('mailto:')) {
        return (
            <Tooltip arrow title="Copy to clipboard">
                <Typography
                    mx={0.5}
                    sx={{ cursor: 'default' }}
                    level="body-sm"
                    color="primary"
                    startDecorator={<EmailIcon />}
                    onClick={() => {
                        navigator.clipboard.writeText(content)
                        Success("Copied to clipboard");
                    }}>
                    {content}
            </Typography>
            </Tooltip >
        );
    }

    const start = content.indexOf(':') > 0 ? content.indexOf('/') + 2 : 0;
    // const end = content.indexOf('/', start) > 0 ? content.indexOf('/', start) : content.length;
    const cropped = content.substring(start, content.length);


    return (

        <Tooltip arrow
            variant="outlined"
            title={<LinkTooltip cropped={cropped} link={content} />}>
                <Typography
                    mx={0.5}
                    color="primary"
                    level="body-sm"
                    sx={{ cursor: 'default' }}
                    startDecorator={<IconLink />}
                    onClick={() => {
                        navigator.clipboard.writeText(content)
                        Success("Copied to clipboard");
                    }}>
                    {cropped}
                </Typography>
        </Tooltip >
    );


};


export function ContentDisplay({content, content_type, original_content=""}: {content: string|React.ReactNode, content_type: string, original_content?: string}) {
    const [showOriginal, setShowOriginal] = React.useState<boolean>(false);

	if(content_type == 'react') {
        return content as React.ReactNode;
    }
	else if(content_type == 'text/plain') {
		return (
            <Typography level="body-md">
            { !showOriginal &&
                <Linkify options={{ render: renderLink }}>{nl2br(content as string)}</Linkify>
            }
            { showOriginal &&
                <Typography level="body-xs" color="neutral">
                    {original_content}
                    </Typography>
            }


            { original_content && original_content != content &&
                <Stack spacing={1} pt={1} className="de-emphasized" >

                        <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end">


                        <Tooltip arrow title="We tried to simplify this message, if it doesn't look right, show the original">
                            <Typography level="body-xs" color="primary"
                                             sx={{ cursor: "pointer" }}
                                             onClick={() => setShowOriginal(!showOriginal)}>
                                             {showOriginal ? 'Hide' : 'Show'} Full Message</Typography>
                                             </Tooltip>

                        </Stack>
                    </Stack>
            }

            </Typography>
        );
	} else if(content_type == 'text/html') {
        console.log('content', content);
        console.log(DOMPurify.sanitize(content as string));
        // console.log('html2text content', htmlToText(content));
        // console.log(DOMPurify.sanitize(content as string));

        return (
            <Stack>

            { showOriginal ? <div dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(content as string)}} /> :
                <Linkify options={{ render: renderLink }}>{nl2br(htmlToText(content as string))}</Linkify> }

                <Tooltip arrow title="We tried to simplify this message, if it doesn't look right, show the original">
                    <Typography level="body-xs" color="primary"
                        sx={{ cursor: "pointer" }}
                        onClick={() => setShowOriginal(!showOriginal)}>
                        {showOriginal ? 'Hide' : 'Show'} Full Message</Typography>
                </Tooltip>

            </Stack>
        );

		// return <div dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(content as string)}} />
	} else {
		return <div>{content_type} {content}</div>
	}
}

function ChatBubbleFileAssociation({file_association}: {file_association: FileAssociationType}) {
    return <FileItem association={file_association} />
}

function ChatBubbleFileAssociations({file_associations}: {file_associations: FileAssociationType[]|undefined}) {
    if(!file_associations) {
        return null;
    }
    return (
        <Stack mt={2} >

        <Stack
                           flexWrap="wrap" useFlexGap
            direction="row"
            alignItems="flex-start"
            spacing={1}
        >
            {file_associations.map((file_association, index) => (
                <ChatBubbleFileAssociation key={index} file_association={file_association} />
            ))}
        </Stack>

        </Stack>
    );

}

function MessageContext({context_type, context_uuid}: {context_type?: string, context_uuid?: string}) {

    const { currentIdentity } = useUserIdentity();
    if(!context_type || !context_uuid) {
        return null;
    }
    const [title, setTitle] = React.useState<string>(capitalize(context_type));
    const { getRequest, fetchRequest } = useFHRequest();
    const request = getRequest(context_uuid);


    useEffect(() => {
        if (context_type == 'request') {
            if (request) {
                setTitle(request.title);
            }
            else {
                fetchRequest(context_uuid);
            }
        }
    }, [request]);


    if(currentIdentity.role != 'assistant') {
        return null;
    }

    const tt_body =
        <Stack>
            <Stack direction="row" spacing={0.4} alignItems="center">
                <Typography level="body-xs" color="neutral">
Working on {capitalize(context_type)}:
                </Typography>
                <Typography level="body-xs" color="primary">
                <Link to={`/${context_type}/${context_uuid}`}>
                {title}
                </Link>
                </Typography>

            </Stack>
        </Stack>;


    // const ContextLink = getIcon('DayOnesClockIn');
    return (
        <Tooltip arrow title={tt_body} variant='outlined'>
            <Typography level="body-xs" color="neutral">
                <Link to={`/${context_type}/${context_uuid}`}>
                <Chip size="sm" sx={{textTransform: 'capitalize'}}>{context_type}</Chip>
                </Link>
            </Typography>
        </Tooltip>
    );
}

export function ChatBubbleSmall({
    content,
	content_type,
    date_created,
    identityKey,
    file_associations,
    context_type,
    context_uuid,
    omitIdentity
}: ChatBubbleProps) {

    return (
        <Stack
            justifyContent="flex-start"
            spacing={1}
        >

            <Stack direction="row" alignItems="flex-start" spacing={1.5}>

                <Stack pt={0.5} sx={{ width: '42px' }}>
                    {(identityKey && !omitIdentity) ?
                        <IdentityAvatar
                            interactive={"link"}
                            identityKey={identityKey}
                            size="avatar" /> :
                        null
                    }
                </Stack>

                <Stack width="100%">
                    {!omitIdentity &&
                        <Stack direction="row" alignItems="center"
                            justifyContent="space-between" spacing={1}>

                            <Stack direction="row" alignItems="center" spacing={1}>

                            {(identityKey && !omitIdentity) ?
                                <Typography level="title-sm" >
                                    <IdentityAvatar
                                        interactive={"link"}
                                        identityKey={identityKey}
                                        size="name" />
                                </Typography> : <Stack></Stack>
                            }


                            <MessageContext
                                context_type={context_type}
                                 context_uuid={context_uuid} />

                            </Stack>

                            <Typography level="body-xs" >
                                <DateOrTime ts={date_created} />
                            </Typography>
                        </Stack>
                    }
                    <Stack alignItems="flex-start">
                        <ContentDisplay
                            content_type={content_type}
                            content={content} />
                    </Stack>
                    {file_associations && file_associations.length > 0 &&
                        <ChatBubbleFileAssociations file_associations={file_associations} />}

                </Stack>

            </Stack>

        </Stack>


    );

};
export default function ChatBubble({
    uuid,
    sender,
    content,
	content_type,
    original_content,
    date_created,
    identityKey,
    file_associations
}: ChatBubbleProps) {
    // const isSent = variant === 'sent';
    const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
    const { rateItem } = useRating();
    const { currentIdentity } = useUserIdentity();

    const HideIcon = getIcon('Close');
    const CopyIcon = getIcon('ContentCopy');
    const [highlighted, setHighlighted] = React.useState<boolean>(false);

    const [score, setScore] = React.useState<number>(0);
    const [showRating, setShowRating] = React.useState<boolean>(false);

    const location = useLocation();
    useEffect(() => {
        if(location.hash === `#${uuid}`) {
            setHighlighted(true);
        }
        else if(highlighted) {
            setHighlighted(false);
        }

    }, [location]);

    if(!currentIdentity) {
        return null;
    }

    return (

        <Sheet
            id={uuid}
            variant={highlighted ? 'outlined' : undefined}
            color={highlighted ? 'primary' : undefined}
            sx={{
                backgroundColor: 'var(--BackgroundColor)',
                p: 1,
                borderRadius: 'sm',

            }}
        >
            <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                spacing={1}
                sx={{

                    mb: 0.25,
                    '--Avatar-size': '32px',
                }}
            >

            <Stack direction="row"
                 spacing={1}
                 alignItems="center"
                 divider={<Divider orientation="vertical" />} >

                {identityKey &&
                    <Typography
                        level="body-sm"
                        color="neutral">
                    <IdentityAvatar
                        interactive={"link"}
                        identityKey={identityKey}
                        size="inherit" />
                    </Typography>
                }
                {sender &&
                    <Typography
                        level="body-xs"
                        color="neutral"
                    >{sender}</Typography>
                }
                </Stack>
                <Box flexGrow={1} />


                <Typography level="body-sm" >
                    <TimeUI ts={date_created} />
                </Typography>

                {/*
                {!showRating &&
                    <Button
                        onClick={() => setShowRating(true)}
                        variant="plain"
                        color="primary"
                        size="sm">Rate</Button>}
                { showRating &&
                    <Button
                        onClick={() => setShowRating(false)}
                        variant="plain"
                        color="primary"
                        size="sm">Cancel</Button>}
                  */}
            </Stack>
                <Box
                    sx={{ position: 'relative' }}
                >
                {showRating &&
                    <Stack mt={1}>
                        <RatingCardThumbs
                            label={<Typography level="title-sm">
                                Rate this message with
                                <IdentityAvatar
                                    size="chip"
                                    interactive={false}
                                    identityKey={identityKey} />
                            </Typography>}
                            setScore={setScore}
                            score={score} />
                        {score != 0 &&
                            <Button
                                onClick={() => {
                                    identityKey && uuid && rateItem(uuid, score, currentIdentity?.uuid, identityKey).then(() => {
                                        Success('Rating submitted');
                                        setScore(0);
                                        setShowRating(false);
                                    });

                                }}
                                variant="plain"
                                color="primary"
                                size="sm">Submit</Button>}

                    </Stack>
                }
                    <Sheet

                        sx={{
                            mt: 1,
                            py: 1.25,
                            backgroundColor: 'var(--BackgroundColor)',

                            // borderRadius: 'sm',
                            // borderTopRightRadius: isSent ? 0 : 'sm',
                            // borderTopLeftRadius: isSent ? 'sm' : 0,
                        }}
						style={{whiteSpace:"pre-wrap"}}
                    >

					<ContentDisplay
                         original_content={original_content}
                            content_type={content_type}
                            content={content} />
                        { file_associations && file_associations.length > 0 &&
                            <ChatBubbleFileAssociations file_associations={file_associations} /> }
                        { isExpanded &&
                            <Stack
                                alignItems="center"
                                direction="row"
                                justifyContent="flex-end"
                                spacing={1}
                                mt={2} >
                            <Tooltip arrow title="Copy this message to your clipboard">
                                <IconButton
                                    size="sm"
                                    variant="plain"
                                    onClick={() => {
                                        navigator.clipboard.writeText(content as string);
                                        Success('Copied to clipboard');
                                    }}>
                                    <CopyIcon />
                                </IconButton>
                            </Tooltip>

                                <Tooltip arrow title="Hide">
                                <IconButton
                                    size="sm"
                                    variant="plain"
                                    onClick={() => setIsExpanded(false) }>
                                    <HideIcon />
                                </IconButton>
                                </Tooltip>
                            </Stack>
                        }

                    </Sheet>

                </Box>
            </Sheet>


    );
}
